import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Pagination,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import Close from '@mui/icons-material/Close';
import PageLayout from '../../components/layout/PageLayout';
import SEO from '../../components/common/SEO';
import { feedsApi } from '../../api/feeds';
import FeedCard from './FeedCard';

export default function FeedList() {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const page = Math.max(1, parseInt(params.get('page'), 10) || 1);
  const [input, setInput] = useState(query);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const key = `${query}:${page}:${attempt}`;
  const data = result?.key === key ? result.data : null;
  useEffect(() => {
    if (data && page > Math.max(1, data.totalPages || 1)) {
      setParams(
        { ...(query ? { q: query } : {}), page: String(Math.max(1, data.totalPages || 1)) },
        { replace: true },
      );
    }
  }, [data, page, query, setParams]);
  useEffect(() => setInput(query), [query]);
  useEffect(() => {
    if (input.trim() === query) return undefined;
    const timer = setTimeout(
      () =>
        setParams({ ...(input.trim() ? { q: input.trim() } : {}), page: '1' }, { replace: true }),
      250,
    );
    return () => clearTimeout(timer);
  }, [input, query, setParams]);
  useEffect(() => {
    const controller = new AbortController();
    setError(false);
    feedsApi.list(page, query, controller.signal).then(
      (data) => {
        if (!controller.signal.aborted) setResult({ key, data });
      },
      () => {
        if (!controller.signal.aborted) setError(true);
      },
    );
    return () => controller.abort();
  }, [page, query, key]);
  return (
    <PageLayout maxWidth="960px">
      <SEO
        title="피드"
        description="포스트팝이 전하는 음악과 문화 이야기"
        url={`${window.location.origin}/feed`}
      />
      <Stack spacing={3}>
        <Typography component="h1" variant="h5" align="center" fontWeight={700}>
          피드
        </Typography>
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="피드 검색"
          inputProps={{ 'aria-label': '피드 검색' }}
          InputProps={{
            sx: { borderRadius: 999 },
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: input ? (
              <InputAdornment position="end">
                <IconButton aria-label="검색어 지우기" onClick={() => setInput('')}>
                  <Close />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        {error ? (
          <Alert
            severity="error"
            action={<Button onClick={() => setAttempt((n) => n + 1)}>다시 시도</Button>}
          >
            피드를 불러오지 못했습니다.
          </Alert>
        ) : !data ? (
          <Box role="status" aria-label="피드 불러오는 중" sx={{ textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : !data.items.length ? (
          <Typography align="center">
            {query ? '검색 결과가 없습니다.' : '아직 등록된 피드가 없습니다.'}
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 3,
            }}
          >
            {data.items.map((post) => (
              <FeedCard key={post.id} post={post} />
            ))}
          </Box>
        )}
        {data?.totalPages > 1 && (
          <Pagination
            count={data.totalPages}
            page={page}
            onChange={(_, value) => {
              setParams({ ...(query ? { q: query } : {}), page: String(value) });
              window.scrollTo({ top: 0, behavior: 'auto' });
            }}
            sx={{ alignSelf: 'center' }}
          />
        )}
      </Stack>
    </PageLayout>
  );
}
